// SPDX-License-Identifier: AGPL-3.0-or-later

import styles from '@app/features/messaging/components/modals/media_modal/pan_zoom/PanZoomSurface.module.css';
import {
	type UsePanZoomSurfaceOptions,
	usePanZoomSurface,
} from '@app/features/messaging/components/modals/media_modal/pan_zoom/usePanZoomSurface';
import {clsx} from 'clsx';
import {motion} from 'framer-motion';
import type {HTMLAttributes, ReactNode} from 'react';
import {forwardRef, memo, useImperativeHandle} from 'react';

export interface PanZoomSurfaceHandle {
	reset: () => void;
	zoomIn: () => void;
	zoomOut: () => void;
}

interface PanZoomSurfaceProps
	extends Omit<
			HTMLAttributes<HTMLDivElement>,
			'children' | 'onDoubleClick' | 'onPointerDown' | 'onPointerMove' | 'onPointerUp' | 'onWheel'
		>,
		UsePanZoomSurfaceOptions {
	children: ReactNode;
	contentClassName?: string;
	contentRole?: string;
}

function formatTransformLength(value: number | string): string {
	return typeof value === 'number' ? `${value.toFixed(3)}px` : value;
}

function formatTransformScale(value: number | string): string {
	return typeof value === 'number' ? value.toFixed(5) : value;
}

function buildPanZoomTransform({
	x = 0,
	y = 0,
	scale = 1,
}: {
	x?: number | string;
	y?: number | string;
	scale?: number | string;
}): string {
	const translate = `translate3d(${formatTransformLength(x)}, ${formatTransformLength(y)}, 0)`;
	return `${translate} scale(${formatTransformScale(scale)})`;
}

export const PanZoomSurface = memo(
	forwardRef<PanZoomSurfaceHandle, PanZoomSurfaceProps>(function PanZoomSurface(
		{
			children,
			className,
			contentClassName,
			contentRole,
			style,
			zoomState,
			maxScale,
			zoomedScale,
			disabled,
			panDisabled,
			doubleClickEnabled,
			tapToToggleZoom,
			resetKey,
			onZoomStateChange,
			onTransformChange,
			onTap,
			onBackdropTap,
			...rest
		}: PanZoomSurfaceProps,
		ref,
	) {
		const controllerOptions: UsePanZoomSurfaceOptions = {
			zoomState,
			maxScale,
			zoomedScale,
			disabled,
			panDisabled,
			doubleClickEnabled,
			tapToToggleZoom,
			resetKey,
			onZoomStateChange,
			onTransformChange,
			onTap,
			onBackdropTap,
		};
		const controller = usePanZoomSurface(controllerOptions);
		useImperativeHandle(
			ref,
			() => ({
				reset: controller.reset,
				zoomIn: controller.zoomIn,
				zoomOut: controller.zoomOut,
			}),
			[controller.reset, controller.zoomIn, controller.zoomOut],
		);
		return (
			<div
				data-flx="messaging.media-modal.pan-zoom.pan-zoom-surface.surface"
				{...rest}
				{...controller.viewportBindings}
				className={clsx(styles.surface, disabled && styles.surfaceDisabled, className)}
				style={{...style, cursor: controller.cursor}}
				data-zoom-state={controller.zoomState}
				data-dragging={controller.isDragging ? 'true' : undefined}
			>
				<motion.div
					data-flx="messaging.media-modal.pan-zoom.pan-zoom-surface.content"
					{...controller.contentBindings}
					className={clsx(styles.content, controller.isDragging && styles.contentDragging, contentClassName)}
					style={{
						x: controller.x,
						y: controller.y,
						scale: controller.scale,
					}}
					transformTemplate={buildPanZoomTransform}
					role={contentRole}
				>
					{children}
				</motion.div>
			</div>
		);
	}),
);
